import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import styles from 'assets/jss/material-dashboard-pro-react/views/bulkPurchase/deviceCardStyles';
import ColorSlider, { ColorPickerPosition } from '../ColorPicker/ColorSlider';
import { IAvailableColorWithId, IAvailableValueItem, IProductByIdItem } from 'utils/api/models/eshop/products';
import { ICalculatedProduct } from 'utils/api/models/vouchers/calculateInstallment';
import { BodySBold, DescriptionS } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import StorageSelector from '../StorageSelector/StorageSelector';
import PriceTag from '../PriceTag';
import { roundMoney } from 'utils/converters/roundMoney';
// @ts-ignore
const useStyles = makeStyles(styles);

export const ITEMS_SEPARATOR = 5;
export const ITEM_SIZE = 25;

interface IDeviceCardProps {
	calculatedProduct?: ICalculatedProduct | undefined;
	availableColors?: IAvailableColorWithId[] | [];
	availableCapacity?: IAvailableValueItem[] | [];
	productVariant?: IProductByIdItem | undefined;
	onColorChange: (id: string) => void;
	onCapacityChange: (id: string) => void;
	onAvailableDevice: (isAvailable: boolean) => void;
}

const DeviceCard = ({
	calculatedProduct,
	availableColors,
	availableCapacity,
	productVariant,
	onColorChange,
	onCapacityChange,
	onAvailableDevice,
}: IDeviceCardProps) => {
	const classes = useStyles();
	const carouselVisible: boolean = true;

	const [selectedColor, setSelectedColor] = useState<string>('');
	const [selectedCapacity, setSelectedCapacity] = useState<string>('');
	const [selectedAvailableColor, setSelectedAvailableColor] = useState<IAvailableColorWithId>();
	const [selectedAvailableCapacity, setSelectedAvailableCapacity] = useState<IAvailableValueItem>();
	const [availableCapacities, setAvailableCapacities] = useState<IAvailableValueItem[]>([]);

	useEffect(() => {
		const colorId = productVariant?.colorId;
		const capacityId = productVariant?.capacityId;
		const color = (availableColors || []).find((color) => color?.id === colorId);
		const capacity = (availableCapacity || []).find((capacity) => capacity?.id === capacityId);
		setSelectedAvailableColor(color);
		setSelectedColor(color?.color || '');
		setSelectedAvailableCapacity({
			...capacity,
			id: capacity?.id || '',
			label: capacity?.label || '',
			available: !!(color?.availableCapacity || []).find((avCapId) => (avCapId || '') === capacity?.id),
		});
		onAvailableDevice(!!(color?.availableCapacity || []).find((avCapId) => (avCapId || '') === capacity?.id));
		setAvailableCapacities(
			(availableCapacity || [])
				.map((cap) => {
					return {
						...cap,
						available: !!(color?.availableCapacity || []).find((avCapId) => (avCapId || '') === cap?.id),
					};
				})
				.sort(sortCapacity)
				.sort(additionalSortCapacity)
		);
		setSelectedCapacity(capacity?.id || '');
	}, [productVariant, availableCapacity, availableColors, onAvailableDevice]);

	const sortCapacity = (a: IAvailableValueItem, b: IAvailableValueItem) => {
		const x = +a.label.split(' ')[0];
		const y = +b.label.split(' ')[0];
		if (x < y) {
			return -1;
		}
		if (x > y) {
			return 1;
		}
		return 0;
	};

	const additionalSortCapacity = (a: IAvailableValueItem, b: IAvailableValueItem) => {
		if (a.label.includes('GB') && b.label.includes('TB')) {
			return -1;
		}
		if (a.label.includes('TB') && b.label.includes('GB')) {
			return 1;
		}
		return 0;
	};

	useEffect(() => {
		if (selectedColor) {
			const color = (availableColors || []).find((color) => color?.color === selectedColor);
			setSelectedAvailableColor(color);
			onColorChange(color?.id || '');
		}
	}, [selectedColor, availableColors, onColorChange]);

	useEffect(() => {
		if (selectedCapacity) {
			const capacity = (availableCapacity || []).find((capacity) => capacity?.id === selectedCapacity);
			setSelectedAvailableCapacity(capacity);
			onCapacityChange(capacity?.id || '');
		}
	}, [selectedCapacity, availableCapacity, onCapacityChange]);

	return (
		<div className={classes.container}>
			<div className={classes.photoContainer}>
				<img alt="product" className={classes.image} src={productVariant?.thumbnail || ''} />
			</div>
			<div className={classes.contentContainer}>
				<div>
					<PriceTag
						currency={translate('OMR')}
						discount={Number(calculatedProduct?.device?.saleDetails?.standardPrice) || 0}
						price={Number(calculatedProduct?.device?.saleDetails?.taxableAmount) || 0}
					/>
					<div className={classes.description}>
						<DescriptionS inline={true} disableMargin color="disabled-light">
							{translate(
								'purchase.bulk.devices.price.vat',
								String(calculatedProduct?.device?.saleDetails?.vatPercentage || 0)
							)}
						</DescriptionS>
						<div className={classes.bold}>
							<DescriptionS inline={true} disableMargin color="disabled-light">
								{translate(
									'price',
									roundMoney(calculatedProduct?.device?.saleDetails?.totalAmount || 0)
								)}
							</DescriptionS>
						</div>
					</div>
				</div>
				<div className={classes.divider}></div>
				<div>
					<div className={classes.storage}>
						<BodySBold disableMargin color="disabled-light">
							{translate('purchase.bulk.devices.storage', selectedAvailableCapacity?.label || '')}
						</BodySBold>
						<StorageSelector
							storages={availableCapacities}
							selectedStorage={selectedCapacity}
							setSelectedStorage={setSelectedCapacity}
						/>
					</div>
					<div className={classes.color}>
						<BodySBold disableMargin color="disabled-light">
							{translate('purchase.bulk.devices.color', selectedAvailableColor?.value || '')}
						</BodySBold>
						<ColorSlider
							position={ColorPickerPosition.HORIZONTAL}
							itemSize={ITEM_SIZE}
							itemsSeparator={ITEMS_SEPARATOR}
							numberOfItemsOnSlide={availableColors?.length || 0}
							setSelectedColor={setSelectedColor}
							selectedColor={selectedColor}
							colors={availableColors || []}
							carouselVisible={carouselVisible}
						/>
					</div>

					<div className={classes.description}>
						<DescriptionS color="secondary-light" disableMargin>
							{productVariant?.shortDescription}
						</DescriptionS>
					</div>
				</div>
			</div>
		</div>
	);
};

export default DeviceCard;
